import type { Patient, Reference } from '@medplum/fhirtypes'
import type { Diagnosis } from '@seen/core/models/conditions/Diagnosis'
import { type JSX, type ReactNode, useState } from 'react'
import type { ExternalToast } from 'sonner'
import type { ConditionPickerCondition } from '#src/components/condition-picker/types.ts'
import { ConditionsCommand } from '#src/components/ConditionsCommand.tsx'
import { Popover } from '#src/components/core/popover/Popover.tsx'
import { PopoverContent } from '#src/components/core/popover/PopoverContent.tsx'
import { PopoverTrigger } from '#src/components/core/popover/PopoverTrigger.tsx'

interface ConditionsComboboxProps {
  patientRef: Reference<Patient> | undefined
  onConditionSelected?: (condition: ConditionPickerCondition) => void
  children: ReactNode
  /** Override the default toast position (e.g. 'top-right' to avoid overlapping fixed bottom buttons) */
  toastPosition?: ExternalToast['position']
  // When set, selecting a condition replaces this existing diagnosis in place
  // instead of adding a new one to the participant.
  replaceTarget?: Diagnosis
  align?: 'start' | 'center' | 'end'
  // Optionally control the popover so a second trigger (e.g. an overflow-menu
  // item) can open the picker anchored to the same children element.
  open?: boolean
  onOpenChange?: (open: boolean) => void
}

export function ConditionsCombobox({
  patientRef,
  onConditionSelected,
  children,
  toastPosition,
  replaceTarget,
  align = 'start',
  open: controlledOpen,
  onOpenChange,
}: ConditionsComboboxProps): JSX.Element {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false)
  const open = controlledOpen ?? uncontrolledOpen
  const setOpen = onOpenChange ?? setUncontrolledOpen

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent
        className="w-[min(36rem,calc(100vw-2rem))] p-0"
        align={align}
      >
        <ConditionsCommand
          patientRef={patientRef}
          toastPosition={toastPosition}
          replaceTarget={replaceTarget}
          onConditionSelected={(condition) => {
            setOpen(false)
            onConditionSelected?.(condition)
          }}
        />
      </PopoverContent>
    </Popover>
  )
}
